import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import ConsentBanner from './components/ConsentBanner';
import { trackPageView, trackOnce } from './lib/metaPixel';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import OnboardingScreen from './components/OnboardingScreen';
import AuthForm from './components/AuthForm';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Feed from './components/Feed';
import RightSidebar from './components/RightSidebar';
import MobileNav from './components/MobileNav';
import ProfilePage from './components/ProfilePage';
import AdminPage from './components/AdminPage';
import CourseBuilder from './components/CourseBuilder';
import SpaceRoute from './components/SpaceRoute';
import DashboardPage from './components/DashboardPage';
import BadgesPage from './components/BadgesPage';
import PublicProfilePage from './components/PublicProfilePage';
import PostPage from './components/PostPage';
import SiteGate from './components/SiteGate';
import WallpaperManager from './components/WallpaperManager';
import PremiumPage from './components/PremiumPage';
import MessagesPage from './components/MessagesPage';
import ShahadahCertificatePage from './components/ShahadahCertificatePage';
import ResetPasswordPage from './components/ResetPasswordPage';
import MentorDiscoveryPage from './components/MentorDiscoveryPage';
import MentorHubPage from './components/MentorHubPage';
import OnboardingFlow from './components/OnboardingFlow';
import Stars from './components/Stars';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import MentorTermsPage from './components/MentorTermsPage';
import DeleteDataPage from './components/DeleteDataPage';
import ChildSafetyPage from './components/ChildSafetyPage';
import AboutPage from './components/AboutPage';
import WifeyBuilderPage from './components/wifey/WifeyBuilderPage';
import QuizPage from './components/quiz/QuizPage';
import LibraryPage from './components/LibraryPage';
import { useLibraryEnabled } from './hooks/useLibraryEnabled';
import CalendarPage from './components/CalendarPage';
import EventPage from './components/EventPage';
import { useEventsEnabled } from './hooks/useEventsEnabled';
import { saveReturnTo, takeReturnTo } from './lib/eventsFormat';
import * as api from './lib/api';
import './App.css';

// Maps legacy page string → URL path
const pageToPath = (page) => {
  if (page === 'feed') return '/';
  if (page === 'dashboard') return '/dashboard';
  if (page === 'profile') return '/profile';
  if (page === 'onboarding') return '/onboarding';
  if (page === 'badges') return '/badges';
  if (page === 'shahadah-certificate') return '/shahadah-certificate';
  if (page === 'premium') return '/premium';
  if (page === 'messages') return '/messages';
  if (page.startsWith('messages:')) return `/messages/${page.slice('messages:'.length)}`;
  if (page === 'mentors') return '/mentors';
  if (page === 'mentor-hub') return '/mentor-hub';
  if (page === 'admin') return '/admin';
  if (page === 'course-builder') return '/admin/course-builder';
  if (page === 'wallpaper-manager') return '/admin/wallpaper-manager';
  if (page === 'privacy') return '/privacy';
  if (page === 'terms') return '/terms';
  if (page === 'mentor-terms') return '/mentor-terms';
  if (page === 'delete-data') return '/delete-data';
  if (page === 'child-safety') return '/child-safety';
  if (page === 'about') return '/about';
  if (page === 'wifey') return '/wifey-application-builder';
  if (page === 'marriage-quiz') return '/marriage-readiness-quiz';
  if (page === 'library') return '/library';
  if (page.startsWith('user:')) return `/u/${page.slice('user:'.length)}`;
  if (page.startsWith('space:')) return `/spaces/${page.slice('space:'.length)}`;
  if (page.startsWith('post:')) return `/posts/${page.slice('post:'.length)}`;
  if (page === 'calendar') return '/calendar';
  if (page.startsWith('event:')) return `/events/${page.slice('event:'.length)}`;
  return '/';
};

// Maps URL pathname → legacy page string (used for Sidebar/MobileNav active state)
const pathToPage = (pathname) => {
  if (pathname === '/' || pathname === '') return 'feed';
  if (pathname === '/dashboard') return 'dashboard';
  if (pathname === '/profile') return 'profile';
  if (pathname === '/onboarding') return 'onboarding';
  if (pathname === '/badges') return 'badges';
  if (pathname === '/shahadah-certificate') return 'shahadah-certificate';
  if (pathname === '/premium') return 'premium';
  if (pathname === '/messages') return 'messages';
  if (pathname.startsWith('/messages/')) return `messages:${pathname.slice('/messages/'.length)}`;
  if (pathname === '/mentors') return 'mentors';
  if (pathname === '/mentor-hub') return 'mentor-hub';
  if (pathname === '/admin') return 'admin';
  if (pathname === '/admin/course-builder') return 'course-builder';
  if (pathname === '/admin/wallpaper-manager') return 'wallpaper-manager';
  if (pathname === '/privacy') return 'privacy';
  if (pathname === '/terms') return 'terms';
  if (pathname === '/mentor-terms') return 'mentor-terms';
  if (pathname === '/delete-data') return 'delete-data';
  if (pathname === '/child-safety') return 'child-safety';
  if (pathname === '/about') return 'about';
  if (pathname.toLowerCase() === '/wifey-application-builder') return 'wifey';
  if (pathname.toLowerCase() === '/marriage-readiness-quiz') return 'marriage-quiz';
  if (pathname.toLowerCase() === '/library') return 'library'; // /Library works too
  if (pathname.startsWith('/u/')) return `user:${pathname.slice('/u/'.length)}`;
  if (pathname.startsWith('/spaces/')) return `space:${pathname.slice('/spaces/'.length)}`;
  if (pathname.startsWith('/posts/')) return `post:${pathname.slice('/posts/'.length)}`;
  if (pathname === '/calendar') return 'calendar';
  if (pathname.startsWith('/events/')) return `event:${pathname.slice('/events/'.length)}`;
  return 'feed';
};

const OnboardingFlowWrapper = ({ onComplete }) => {
  const { profile } = useAuth();
  const [existingData, setExistingData] = React.useState(null);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!profile?.id) return;
    const fetchExisting = async () => {
      try {
        const res = await api.getOnboarding(profile.id);
        if (res.data) setExistingData(res.data);
      } catch (e) { /* first time */ }
      setLoaded(true);
    };
    fetchExisting();
  }, [profile?.id]);

  // A member with no saved answers yet has just created their account
  React.useEffect(() => {
    if (loaded && !existingData && profile?.id) {
      trackOnce(`sirati_meta_reg_${profile.id}`, 'CompleteRegistration', { eventId: `reg_${profile.id}` });
    }
  }, [loaded, existingData, profile?.id]);

  if (!loaded) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-pulse text-text-muted">Loading...</div>
    </div>
  );

  return <OnboardingFlow onComplete={onComplete} existingData={existingData} />;
};

// Events pages stay hidden from members until an admin turns the feature on (admins can always preview).
const EventsRoute = ({ children }) => {
  const { profile } = useAuth();
  const enabled = useEventsEnabled();
  if (profile?.is_admin === true || enabled === true) return children;
  if (enabled === null) return <div className="p-8 text-center text-text-muted animate-pulse">Loading...</div>;
  return (
    <div className="max-w-[600px] mx-auto p-8 text-center" data-testid="events-coming-soon">
      <h1 className="font-heading text-2xl font-semibold text-primary mb-2">Events are coming soon</h1>
      <p className="text-text-muted">We are putting the finishing touches on in-person and online events for the community.</p>
    </div>
  );
};

// The Library stays hidden from members until an admin turns it on (admins can always preview).
const LibraryRoute = ({ children }) => {
  const { profile } = useAuth();
  const enabled = useLibraryEnabled();
  if (profile?.is_admin === true || enabled === true) return children;
  if (enabled === null) return <div className="p-8 text-center text-text-muted animate-pulse">Loading...</div>;
  return (
    <div className="max-w-[600px] mx-auto p-8 text-center" data-testid="library-coming-soon">
      <h1 className="font-heading text-2xl font-semibold text-primary mb-2">The Library is coming soon</h1>
      <p className="text-text-muted">We are stocking the shelves with guides, tools and resources for your journey.</p>
    </div>
  );
};

const MainApp = () => {
  const { user, loading, hasSeenOnboarding, completeOnboarding, onboardingComplete, markOnboardingComplete, isPasswordRecovery, clearPasswordRecovery } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [challengePreFill, setChallengePreFill] = useState(null);
  const [welcome, setWelcome] = useState(null); // { paid } after onboarding, dismissed by the member
  const [authMode, setAuthMode] = useState('signup'); // which side of AuthForm opens first
  const navigate = useNavigate();
  const location = useLocation();

  // Meta Pixel page views (a no-op until the member accepts the cookie banner)
  useEffect(() => { trackPageView(); }, [location.pathname]);

  const currentPage = pathToPage(location.pathname);

  // A visitor who came for an event lands back on it once signup and onboarding are finished
  useEffect(() => {
    if (user && onboardingComplete === true) {
      const path = takeReturnTo();
      if (path) navigate(path, { replace: true });
    }
  }, [user, onboardingComplete, navigate]);

  // Fire-and-forget seed call. Dev-only convenience — MUST NOT block the UI.
  useEffect(() => {
    api.seedData().catch(() => { /* already seeded or unreachable — fine */ });
  }, []);

  // Register periodic background sync for daily dua + challenge refresh
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready.then(async (reg) => {
      if (!('periodicSync' in reg)) return;
      try {
        await reg.periodicSync.register('refresh-daily-content', {
          minInterval: 24 * 60 * 60 * 1000,
        });
      } catch {
        // Browser may deny if the user hasn't engaged with the site enough — silent fail
      }
    });
  }, []);

  // Capture /invite/:code paths so users who follow a shared link land on signup
  // with their referrer's code stored, then redirect to root.
  useEffect(() => {
    const match = location.pathname.match(/^\/invite\/([^/]+)/);
    if (match) {
      const code = decodeURIComponent(match[1]);
      sessionStorage.setItem('sirati_invite_ref', code);
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleNavigate = (page, data) => {
    setSearchQuery('');
    if (page === 'feed' && data?.challengeText) {
      setChallengePreFill(data.challengeText);
    } else {
      setChallengePreFill(null);
    }
    navigate(pageToPath(page));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-semibold text-primary mb-4">Sirati</h1>
          <div className="animate-pulse text-text-muted">Loading your community...</div>
        </div>
      </div>
    );
  }

  // Password-reset flow — intercept before all other routing so the user
  // can set their new password immediately after clicking the email link.
  if (isPasswordRecovery) {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <ResetPasswordPage onComplete={() => { clearPasswordRecovery(); handleNavigate('feed'); }} />
      </div>
    );
  }

  // Public pages — no auth required
  if (currentPage === 'privacy') {
    return <PrivacyPage onNavigate={handleNavigate} />;
  }
  if (currentPage === 'terms') {
    return <TermsPage onNavigate={handleNavigate} />;
  }
  if (currentPage === 'mentor-terms') {
    return <MentorTermsPage onNavigate={handleNavigate} />;
  }
  if (currentPage === 'delete-data') {
    return <DeleteDataPage onNavigate={handleNavigate} />;
  }
  if (currentPage === 'child-safety') {
    return <ChildSafetyPage onNavigate={handleNavigate} />;
  }
  if (currentPage === 'wifey') {
    return <WifeyBuilderPage />;
  }
  if (currentPage === 'marriage-quiz') {
    return <QuizPage />;
  }

  if (currentPage === 'about') {
    return <AboutPage onNavigate={handleNavigate} />;
  }

  // Public event page: a teaser for sharing and ads. Registering needs an account.
  if (!user && currentPage.startsWith('event:')) {
    const eventId = currentPage.slice('event:'.length);
    const startAuth = (mode) => {
      saveReturnTo(`/events/${eventId}`);
      setAuthMode(mode);
      completeOnboarding();
      navigate('/');
    };
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <div className="relative z-10">
          <EventPage
            eventId={eventId}
            member={false}
            onBack={() => navigate('/')}
            onSignUp={() => startAuth('signup')}
            onSignIn={() => startAuth('login')}
          />
        </div>
      </div>
    );
  }

  if (!hasSeenOnboarding && !user) {
    return (
      <SiteGate>
        <OnboardingScreen
          onNavigate={handleNavigate}
          onComplete={completeOnboarding}
          onSignIn={() => { setAuthMode('login'); completeOnboarding(); }}
        />
      </SiteGate>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 relative garden-bg overflow-hidden">
        <Stars />
        <div className="relative z-10">
          <SiteGate>
            <AuthForm onNavigate={handleNavigate} initialMode={authMode} />
          </SiteGate>
        </div>
      </div>
    );
  }

  // Profile still loading after auth — wait before making routing decisions
  if (onboardingComplete === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-semibold text-primary mb-4">Sirati</h1>
          <div className="animate-pulse text-text-muted">Loading your community...</div>
        </div>
      </div>
    );
  }

  // Forced onboarding — authenticated but no user_onboarding row yet
  // No Header, no navigation — user cannot leave this screen until complete
  if (onboardingComplete === false) {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <OnboardingFlowWrapper
          onComplete={({ paid } = {}) => {
            markOnboardingComplete();
            setWelcome({ paid: !!paid });
            handleNavigate('feed');
          }}
        />
      </div>
    );
  }

  // Onboarding Flow Page (revisit from settings)
  if (currentPage === 'onboarding') {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <OnboardingFlowWrapper onComplete={() => handleNavigate('dashboard')} />
      </div>
    );
  }

  // Profile Page
  if (currentPage === 'profile') {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <ProfilePage
          onBack={() => handleNavigate('feed')}
          onNavigate={handleNavigate}
        />
      </div>
    );
  }

  // Admin Page
  if (currentPage === 'admin') {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <AdminPage onBack={() => handleNavigate('feed')} onNavigate={handleNavigate} />
      </div>
    );
  }

  // Course Builder (Admin only)
  if (currentPage === 'course-builder') {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <CourseBuilder onBack={() => handleNavigate('admin')} />
      </div>
    );
  }

  // Wallpaper Manager (Admin only)
  if (currentPage === 'wallpaper-manager') {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <WallpaperManager onBack={() => handleNavigate('admin')} />
      </div>
    );
  }

  // Premium Page (Stripe subscription)
  if (currentPage === 'premium') {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <PremiumPage onBack={() => handleNavigate('feed')} />
      </div>
    );
  }

  // Mentor Discovery Page
  if (currentPage === 'mentors') {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <MentorDiscoveryPage onBack={() => handleNavigate('feed')} onNavigate={handleNavigate} />
      </div>
    );
  }

  // Mentor Hub (mentors only)
  if (currentPage === 'mentor-hub') {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <MentorHubPage onBack={() => handleNavigate('profile')} />
      </div>
    );
  }

  // Messages Page (premium-gated direct messaging)
  if (currentPage === 'messages' || currentPage.startsWith('messages:')) {
    const presetTargetId = currentPage.startsWith('messages:') ? currentPage.slice('messages:'.length) : null;
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <MessagesPage onBack={() => handleNavigate('feed')} presetTargetId={presetTargetId} />
      </div>
    );
  }

  // Dashboard Page
  if (currentPage === 'dashboard') {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <DashboardPage onBack={() => handleNavigate('feed')} onNavigate={handleNavigate} />
      </div>
    );
  }

  // Badges Page
  if (currentPage === 'badges') {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <BadgesPage onBack={() => handleNavigate('dashboard')} onNavigate={handleNavigate} />
      </div>
    );
  }

  // Shahadah Certificate Page
  if (currentPage === 'shahadah-certificate') {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <ShahadahCertificatePage onBack={() => handleNavigate('badges')} />
      </div>
    );
  }

  // Public Profile Page (/u/:username)
  if (currentPage.startsWith('user:')) {
    const targetUsername = currentPage.slice('user:'.length);
    return (
      <div className="min-h-screen relative">
        <PublicProfilePage
          username={targetUsername}
          onBack={() => handleNavigate('feed')}
          onNavigate={handleNavigate}
        />
      </div>
    );
  }

  // Library (/library): members only, so it sits after the sign-in and onboarding gates
  if (currentPage === 'library') {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <LibraryRoute>
          <LibraryPage onNavigate={handleNavigate} />
        </LibraryRoute>
        <MobileNav onNavigate={handleNavigate} currentPage={currentPage} />
      </div>
    );
  }

  // Events calendar (/calendar) and event pages (/events/:id)
  if (currentPage === 'calendar' || currentPage.startsWith('event:')) {
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <EventsRoute>
          {currentPage === 'calendar' ? (
            <CalendarPage onNavigate={handleNavigate} />
          ) : (
            <EventPage
              eventId={currentPage.slice('event:'.length)}
              member
              onBack={() => handleNavigate('calendar')}
              onNavigate={handleNavigate}
            />
          )}
        </EventsRoute>
        <MobileNav onNavigate={handleNavigate} currentPage={currentPage} />
      </div>
    );
  }

  // Space Page (/spaces/:id)
  if (currentPage.startsWith('space:')) {
    const spaceId = currentPage.slice('space:'.length);
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <SpaceRoute param={spaceId} onBack={() => handleNavigate('feed')} />
      </div>
    );
  }

  // Post Page (/posts/:id)
  if (currentPage.startsWith('post:')) {
    const postId = currentPage.slice('post:'.length);
    return (
      <div className="min-h-screen feed-ombre-bg relative">
        <Stars />
        <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} searchEnabled={false} />
        <PostPage postId={postId} onNavigate={handleNavigate} />
      </div>
    );
  }

  // Main feed layout
  return (
    <div className="min-h-screen feed-ombre-bg relative">
      <Stars />

      <Header onSearchOpen={handleSearch} onSearchClear={() => setSearchQuery('')} onNavigate={handleNavigate} />

      <div className="max-w-[1400px] mx-auto feed-content">
        {welcome && (
          <div className="mx-4 lg:mx-6 mt-4 p-4 bg-primary/10 border border-primary/20 rounded-arch-lg flex items-start gap-3" role="status" data-testid="welcome-banner">
            <Sparkles className="w-5 h-5 text-primary mt-0.5 shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <p className="text-base font-semibold text-primary">{welcome.paid ? 'Welcome to Sirati+!' : 'Welcome to Sirati!'}</p>
              <p className="text-sm text-text-main mt-0.5">
                {welcome.paid
                  ? "You're all set. Say salam, message someone you click with, and make your profile your own."
                  : "You're all set. Choose an avatar and complete your profile so the community can get to know you."}
              </p>
            </div>
            <button onClick={() => setWelcome(null)} aria-label="Dismiss welcome message" className="min-h-[44px] min-w-[44px] -my-2 -mr-2 flex items-center justify-center text-text-muted hover:text-text-main text-xl leading-none">×</button>
          </div>
        )}
        <div className="grid grid-cols-12 gap-0">
          <Sidebar onNavigate={handleNavigate} currentPage={currentPage} />

          <Feed
            searchQuery={searchQuery}
            onClearSearch={() => setSearchQuery('')}
            challengePreFill={challengePreFill}
            onChallengeConsumed={() => setChallengePreFill(null)}
            onNavigate={handleNavigate}
          />

          <RightSidebar onNavigate={handleNavigate} />
        </div>
      </div>

      <MobileNav onNavigate={handleNavigate} currentPage={currentPage} />

      {/* Spacer so feed content scrolls above the fixed mobile nav + home indicator */}
      <div className="h-24 lg:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <MainApp />
          <ConsentBanner />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
